import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import { worksList } from "@/mocks/works";

export default function Detail() {
  const { id } = useParams();
  const work = worksList.find((w) => w.id === id);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            未找到该影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            作品可能已下架或链接有误，试试返回分类页浏览
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const related = worksList
    .filter((w) => w.id !== work.id && (w.type === work.type || w.genre === work.genre))
    .slice(0, 4);

  const infoRows = [
    { label: "类型", value: work.type, icon: "ri-movie-line" },
    { label: "导演", value: work.director, icon: "ri-user-star-line" },
    { label: "主演", value: work.cast.join("、"), icon: "ri-group-line" },
    { label: "上映日期", value: work.releaseDate, icon: "ri-calendar-line" },
    { label: "时长", value: work.duration, icon: "ri-time-line" },
    { label: "地区", value: work.region, icon: "ri-map-pin-line" },
  ];

  return (
    <Layout>
      {/* 顶部信息区 */}
      <section className="relative bg-primary-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={work.cover}
            alt=""
            aria-hidden
            className="w-full h-full object-cover object-top opacity-25 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/70" />
        </div>
        <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: work.type, to: `/category/${work.type}` }, { label: work.title }]} />
          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-white/15">
                <img
                  src={work.cover}
                  alt={`${work.title} 高清封面`}
                  title={`${work.title} - 爱爱网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-3">
                {work.genre}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">
                {work.title}
              </h1>
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <span className="flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-bold text-accent-400">
                    {work.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-white/60">分</span>
                </span>
                <span className="text-sm text-white/70">{work.year}</span>
                <span className="text-sm text-white/70">{work.region}</span>
                <span className="flex items-center gap-1 text-sm text-white/70">
                  <i className="ri-fire-line text-accent-400" />
                  热度 {work.heat.toLocaleString()}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-white/10 border border-white/15 text-xs text-white/85"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-sm md:text-base text-primary-100/85 leading-relaxed">
                {work.synopsis}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setToast("收藏功能待接入，敬请期待")}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-heart-line" />
                  收藏
                </button>
                <button
                  type="button"
                  onClick={() => setToast("分享链接已复制（前端演示）")}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-white/25 hover:bg-white/10 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <Link
                  to={`/category/${work.type}`}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-white/25 hover:bg-white/10 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-links-line" />
                  进入{work.type}分类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 基本信息 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              作品简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {work.synopsis}
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              《{work.title}》是一部{work.year}年出品的{work.region}{work.genre}题材{work.type}，
              由{work.director}执导，{work.cast.slice(0, 2).join("、")}等联袂出演。
              作品自上线以来收获了广泛关注，目前位列平台热度前列，
              被不少观众列入年度必看清单。
            </p>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-5 rounded-full bg-primary-500" />
              基本信息
            </h2>
            <div className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-3 px-4 py-3">
                  <span className="w-7 h-7 rounded-md bg-background-100 text-foreground-500 flex items-center justify-center shrink-0">
                    <i className={`${row.icon} text-sm`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400">{row.label}</p>
                    <p className="text-sm text-foreground-800 break-words">{row.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12 bg-background-100 border-y border-background-200">
        <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
          <span className="w-1.5 h-5 rounded-full bg-accent-500" />
          相关推荐
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      </section>

      {/* 提示 Toast */}
      {toast && (
        <div className="fixed bottom-24 md:bottom-12 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-foreground-950/90 text-white text-sm animate-fade-up whitespace-nowrap">
          <i className="ri-information-line mr-2 text-accent-400" />
          {toast}
        </div>
      )}
    </Layout>
  );
}